/**
 * StepUpModal — password-confirm dialog for sensitive operations (ADR-014 D6).
 *
 * Flow:
 *   1. Registers itself with `stepup.ts` on mount so the axios interceptor
 *      in `auth.ts` can imperatively open it when the backend returns
 *      401 `step_up_required`.
 *   2. User enters their password → `authAPI.reauthenticate()` → on 200,
 *      localStorage is updated with the fresh access_token / refresh_token,
 *      and `fulfillStepUp(newToken)` releases the waiting interceptor which
 *      retries the original request.
 *   3. Cancel / close → `rejectStepUp()` so the interceptor rejects the
 *      original request cleanly (caller sees the ordinary 401).
 *
 * Mounted once at the React root. Only one instance is needed; the stepup
 * coordinator is module-level so it doesn't care which route is active.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';

import { reauthenticate } from '../auth/authService';
import {
  registerStepUpOpener,
  fulfillStepUp,
  rejectStepUp,
  StepUpCancelledError,
} from '../api/stepup';
import { useTranslation } from 'react-i18next';

export const StepUpModal: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Register with the step-up coordinator on mount.
  React.useEffect(() => {
    const unsubscribe = registerStepUpOpener(() => {
      setPassword('');
      setError(null);
      setSubmitting(false);
      setOpen(true);
    });
    return unsubscribe;
  }, []);

  const handleCancel = () => {
    if (submitting) return; // don't abort mid-request
    setOpen(false);
    rejectStepUp(new StepUpCancelledError());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await reauthenticate(password);
      // `reauthenticate()` has already persisted the new access + refresh
      // tokens via storeTokens(), so the platform client will pick them up
      // automatically on retry. We only need to release the coordinator.
      setOpen(false);
      fulfillStepUp(data.access_token);
    } catch (err: unknown) {
      // Don't fulfillStepUp on error — user must retry or cancel.
      const status = (err as { status?: number })?.status;
      if (status === 400) {
        setError(t('common:stepUp.errorIncorrectPassword'));
      } else if (status === 429) {
        setError(
          'Too many attempts. Please wait a few minutes before trying again.',
        );
      } else if (status === 401) {
        // Outer session itself is gone; fall back to sign-in.
        setError(t('common:stepUp.errorSessionExpired'));
      } else {
        setError(t('common:stepUp.errorVerifyFailed'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('common:stepUp.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('common:stepUp.description')}
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('common:stepUp.passwordLabel')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoComplete="current-password"
            required
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={handleCancel} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !password}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {t('common:stepUp.verifying')}
              </Box>
            ) : (
              t('common:stepUp.confirm')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default StepUpModal;
